import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';

/**
 * Route metadata for navigation, page titles, breadcrumbs, and shell layout.
 */
export const ROUTE_META = {
  '/dashboard': {
    title: 'Dashboard',
    navLabel: 'Dashboard',
    showInNav: true,
    icon: DashboardOutlinedIcon,
    contentPadding: 'none',
    fullHeight: true,
  },
  '/content-bank': {
    title: 'Content Bank',
    navLabel: 'Content Bank',
    showInNav: true,
    icon: FolderOutlinedIcon,
    contentPadding: 'none',
    fullHeight: true,
  },
  '/content-creation': {
    title: 'Content Creation',
    navLabel: null,
    showInNav: false,
    icon: EditNoteOutlinedIcon,
    contentPadding: 'none',
    fullHeight: true,
  },
  '/project-content': {
    title: 'Project Content',
    navLabel: 'Projects',
    showInNav: true,
    icon: WorkOutlineOutlinedIcon,
    contentPadding: 'none',
    fullHeight: true,
  },
  '/project-configuration': {
    title: 'Project Configuration',
    navLabel: null,
    showInNav: false,
    icon: SettingsOutlinedIcon,
    contentPadding: 'none',
    fullHeight: true,
  },
  '/master': {
    title: 'Master Data',
    navLabel: 'Master',
    showInNav: false,
    icon: StorageOutlinedIcon,
    contentPadding: 'standard',
    fullHeight: false,
  },
  '/coding-questions': {
    title: 'Coding Questions',
    navLabel: null,
    showInNav: false,
    icon: EditNoteOutlinedIcon,
    contentPadding: 'none',
    fullHeight: true,
  },
};

export const NAV_ITEMS = Object.entries(ROUTE_META)
  .filter(([, meta]) => meta.showInNav)
  .map(([path, meta]) => ({
    path,
    label: meta.navLabel,
    icon: meta.icon,
  }));

export const CONTENT_PADDING = {
  none: { p: 0 },
  compact: { px: 2, py: 1.5 },
  standard: { px: 3, py: 2 },
  comfortable: { px: 3, py: 3 },
};

export function getRouteMeta(pathname) {
  if (pathname.startsWith('/testing/mcq/') || pathname.startsWith('/testing/quiz/')) {
    return {
      title: 'MCQ Testing',
      contentPadding: 'none',
      fullHeight: false,
    };
  }
  return ROUTE_META[pathname] || {
    title: 'LMS Content Creation',
    contentPadding: 'standard',
    fullHeight: false,
  };
}

function readSessionJson(key) {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function readSessionString(key) {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return null;
    const parsed = JSON.parse(value);
    return typeof parsed === 'string' ? parsed : String(parsed);
  } catch {
    return null;
  }
}

/**
 * Build breadcrumb trail from current route and session context.
 */
export function getBreadcrumbs(pathname) {
  if (pathname === '/dashboard') {
    return [{ label: 'Dashboard' }];
  }

  const crumbs = [{ label: 'Dashboard', path: '/dashboard' }];

  if (pathname.startsWith('/testing/mcq/') || pathname.startsWith('/testing/quiz/')) {
    crumbs.push({ label: 'MCQ Testing' });
    return crumbs;
  }

  if (pathname === '/content-bank') {
    crumbs.push({ label: 'Content Bank' });
    const subjectName = readSessionString('contentCreation_subjectName');
    const topicId = readSessionJson('contentBank_topicId');
    const subTopicId = readSessionJson('contentBank_subTopicId');
    if (subjectName) crumbs.push({ label: subjectName });
    if (topicId) crumbs.push({ label: 'Topic' });
    if (subTopicId) crumbs.push({ label: 'Sub-topic' });
    return crumbs;
  }

  if (pathname === '/content-creation') {
    crumbs.push({ label: 'Content Creation' });
    const subjectName = readSessionString('contentCreation_subjectName');
    const projectName = readSessionString('contentCreation_projectName');
    if (projectName) crumbs.push({ label: projectName });
    else if (subjectName) crumbs.push({ label: subjectName });
    return crumbs;
  }

  if (pathname === '/project-content') {
    crumbs.push({ label: 'Projects', path: '/dashboard' });
    const projectName = readSessionString('contentCreation_projectName');
    crumbs.push({ label: projectName || 'Project Content' });
    return crumbs;
  }

  if (pathname === '/project-configuration') {
    crumbs.push({ label: 'Project Configuration' });
    return crumbs;
  }

  if (pathname === '/master') {
    crumbs.push({ label: 'Master Data' });
    return crumbs;
  }

  if (pathname === '/coding-questions') {
    const returnPath = readSessionString('codingQuestions_returnPath') || '/content-bank';
    const returnLabel = returnPath === '/project-content' ? 'Project Content' : 'Content Bank';
    crumbs.push({ label: returnLabel, path: returnPath });
    crumbs.push({ label: 'Coding Questions' });
    return crumbs;
  }

  return crumbs;
}
